import Cookies from "js-cookie";
import {
  getReactBooleanEnv,
  getReactEnvValue,
  isReactStrictEnv,
} from "./envConfig";

/** In-memory fallback when Secure cookies are not readable on http://localhost. */
const JWT_SESSION_KEY = "JWT_TOKEN_SESSION";
const SERVER_SESSION_ACTIVE_KEY = "DRX_SERVER_SESSION_ACTIVE";
const SERVER_SESSION_EXPIRES_KEY = "DRX_SERVER_SESSION_EXPIRES_AT";
const AUTH_SESSION_STORAGE_KEYS = new Set([
  JWT_SESSION_KEY,
  SERVER_SESSION_ACTIVE_KEY,
  SERVER_SESSION_EXPIRES_KEY,
]);
let cachedWsToken = "";
let cachedWsTokenExpiresAt = 0;

/** True when the page is served over HTTPS (including https://localhost). */
export function isSecurePageContext(): boolean {
  return (
    typeof window !== "undefined" && window.location?.protocol === "https:"
  );
}

/**
 * Cookie options for JWT_TOKEN.
 * Secure + SameSite=None only on HTTPS; plain HTTP localhost cannot store Secure cookies reliably.
 * This JavaScript-readable cookie is only ever written when server-session auth is OFF; when it is
 * ON the Hub owns an HttpOnly session instead, which browser JavaScript cannot create.
 */
export function jwtCookieOptions(expires: Date): Cookies.CookieAttributes {
  if (isSecurePageContext()) {
    return { expires, sameSite: "none", secure: true };
  }
  return { expires, sameSite: "lax", secure: false };
}

/**
 * Whether this build keeps the DigitalRx JWT in the browser. Keyed on the auth mode, NOT on the
 * hostname: gating it on "am I on localhost" meant a DEV/STAGE build with
 * REACT_APP_USE_SERVER_SESSION_AUTH=false silently discarded the token at login, so the app
 * authenticated successfully and then had no credential for any subsequent call.
 */
function persistJwtInBrowser(): boolean {
  return !shouldUseServerSessionAuth();
}

export function setJwtTokenCookie(token: string, expires: Date): void {
  const trimmed = (token || "").trim();
  if (!trimmed) {
    return;
  }
  if (!persistJwtInBrowser()) {
    return;
  }
  Cookies.set("JWT_TOKEN", trimmed, jwtCookieOptions(expires));
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(JWT_SESSION_KEY, trimmed);
  }
}

export function clearJwtTokenStorage(): void {
  Cookies.remove("JWT_TOKEN");
  cachedWsToken = "";
  cachedWsTokenExpiresAt = 0;
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(JWT_SESSION_KEY);
    sessionStorage.removeItem(SERVER_SESSION_ACTIVE_KEY);
    sessionStorage.removeItem(SERVER_SESSION_EXPIRES_KEY);
  }
}

/**
 * Decode a JWT's `exp` claim (seconds since epoch) into a Date, or null when the token is
 * unparseable / has no exp. Pure client-side base64url decode — NO signature verification
 * (that stays the backend's job); we only need the expiry to size the cookie and to avoid
 * handing back a token that is already dead.
 */
export function getJwtExpiryDate(token: string): Date | null {
  const parts = (token || "").trim().split(".");
  if (parts.length < 2) {
    return null;
  }
  try {
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4 !== 0) {
      payload += "=";
    }
    const claims = JSON.parse(atob(payload));
    const exp = Number(claims?.exp);
    if (!Number.isFinite(exp) || exp <= 0) {
      return null;
    }
    return new Date(exp * 1000);
  } catch {
    return null;
  }
}

/**
 * True when a JWT's own `exp` has passed. Returns false for tokens we can't parse — we let
 * the server (and the Https.tsx invalid-token interceptor) be the judge rather than nuking a
 * token we merely failed to read.
 */
export function isJwtExpired(token: string): boolean {
  const expiry = getJwtExpiryDate(token);
  if (!expiry) {
    return false;
  }
  return Date.now() >= expiry.getTime();
}

/**
 * Read JWT for orchestration/MCP calls.
 * Uses cookie first; falls back to sessionStorage (set at login).
 * An expired token is never returned: the sessionStorage fallback has no built-in expiry, so
 * without this check a dead token would survive page refreshes and keep triggering 400
 * "Invalid token" until a manual logout. Clearing here also makes hasClientAuthSession()
 * report false, so the app routes to login on its own.
 */
export function getJwtTokenCookie(): string {
  const fromCookie = (Cookies.get("JWT_TOKEN") || "").trim();
  const token =
    fromCookie ||
    (persistJwtInBrowser() && typeof sessionStorage !== "undefined"
      ? (sessionStorage.getItem(JWT_SESSION_KEY) || "").trim()
      : "");
  if (!token) {
    return "";
  }
  if (isJwtExpired(token)) {
    clearJwtTokenStorage();
    return "";
  }
  return token;
}

export function getOrchestrationHttpBaseUrl(): string {
  const explicit =
    getReactEnvValue("REACT_APP_ORCHESTRATION_API_URL") ||
    getReactEnvValue("REACT_APP_API_URL");
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }
  const wsUrl = getReactEnvValue("REACT_APP_ORCHESTRATION_WS_URL", "");
  if (wsUrl) {
    return wsUrl
      .replace(/^wss:/, "https:")
      .replace(/^ws:/, "http:")
      .replace(/\/ws\/?$/, "")
      .replace(/\/+$/, "");
  }
  return "http://localhost:5000";
}

export function rememberWsToken(data: any): string {
  const token = String(data?.ws_token || data?.wsToken || "").trim();
  if (!token) {
    return "";
  }
  cachedWsToken = token;
  const expiresAt = Number(data?.ws_token_expires_at || data?.wsTokenExpiresAt || 0);
  cachedWsTokenExpiresAt = Number.isFinite(expiresAt) ? expiresAt : 0;
  markServerSessionAuthenticated(data?.session_expires_at || data?.sessionExpiresAt);
  return token;
}

export function markServerSessionAuthenticated(expiresAt?: unknown): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(SERVER_SESSION_ACTIVE_KEY, "true");
  const numericExpiresAt = Number(expiresAt || 0);
  if (Number.isFinite(numericExpiresAt) && numericExpiresAt > 0) {
    sessionStorage.setItem(SERVER_SESSION_EXPIRES_KEY, String(numericExpiresAt));
  }
}

export function hasClientAuthSession(): boolean {
  if (!shouldUseServerSessionAuth()) {
    return Boolean(getJwtTokenCookie());
  }
  if (typeof sessionStorage === "undefined") {
    return false;
  }
  if (sessionStorage.getItem(SERVER_SESSION_ACTIVE_KEY) !== "true") {
    return false;
  }
  const expiresAt = Number(sessionStorage.getItem(SERVER_SESSION_EXPIRES_KEY) || 0);
  if (expiresAt && Number.isFinite(expiresAt) && Date.now() / 1000 >= expiresAt) {
    clearJwtTokenStorage();
    return false;
  }
  return true;
}

export function isAuthSessionStorageKey(key: string): boolean {
  return AUTH_SESSION_STORAGE_KEYS.has(key);
}

export async function exchangeServerSession(jwtToken = getJwtTokenCookie()): Promise<string> {
  const token = (jwtToken || "").trim();
  if (!token) {
    return "";
  }
  const response = await fetch(`${getOrchestrationHttpBaseUrl()}/auth/session/exchange`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ jwtToken: token }),
  });
  if (!response.ok) {
    return "";
  }
  return rememberWsToken(await response.json());
}

export async function getWsAuthToken(): Promise<string> {
  if (!isReactStrictEnv()) {
    return "";
  }
  const refresh = await fetch(`${getOrchestrationHttpBaseUrl()}/auth/session/ws-token`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
  if (refresh?.ok) {
    const refreshed = rememberWsToken(await refresh.json());
    if (refreshed) {
      return refreshed;
    }
  }

  return exchangeServerSession();
}

export async function logoutServerSession(): Promise<void> {
  cachedWsToken = "";
  cachedWsTokenExpiresAt = 0;
  await fetch(`${getOrchestrationHttpBaseUrl()}/auth/session/logout`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

export function clearClientAuthSession(): void {
  void logoutServerSession();
  clearJwtTokenStorage();
}

/**
 * Reset the current login session's chat/draft state on the backend WITHOUT ending the
 * login (distinct from logoutServerSession) — the doctor stays logged in and on the same
 * patient, only the conversation/draft is wiped.
 */
export async function clearOrchestrationConversation(): Promise<void> {
  await fetch(`${getOrchestrationHttpBaseUrl()}/auth/session/clear-conversation`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

/** Fires once per page life so parallel in-flight 400s don't trigger competing redirects. */
let expiredAuthRedirectInFlight = false;

/**
 * True when a DigitalRx API response indicates the JWT / session token is invalid or
 * expired. The backend returns HTTP 400 with a body like
 * `{"digitalRxStatusMessage":"Invalid token: "}` — NOT a 401 — so we must match on the
 * message, and only on that message, so ordinary 400 validation errors are left alone.
 */
export function isInvalidTokenResponse(status: number, data: any): boolean {
  if (status !== 400) {
    return false;
  }
  const message = String(
    data?.digitalRxStatusMessage ?? data?.message ?? data ?? ""
  ).toLowerCase();
  return (
    message.includes("invalid token") ||
    message.includes("token expired") ||
    message.includes("expired token")
  );
}

/**
 * Recover from an expired auth token by clearing the stale session and sending the user
 * to the login screen. The DigitalRx JWT is short-lived and there is no silent refresh on
 * the `.do` path, so a plain page refresh just reuses the dead token and keeps 400-ing —
 * only a fresh login mints a new token. Mirrors LogoutSaga's cleanup + redirect-to-"/".
 */
export function handleExpiredAuthSession(): void {
  if (expiredAuthRedirectInFlight) {
    return;
  }
  expiredAuthRedirectInFlight = true;
  try {
    clearClientAuthSession();
  } catch {
    // best-effort: redirect must still happen
  }
  try {
    window.localStorage.clear();
    sessionStorage.clear();
  } catch {
    // best-effort
  }
  // HashRouter login route is "/". Reload to reset all in-memory app state.
  window.location.hash = "#/";
  window.location.reload();
}

export function shouldUseServerSessionAuth(): boolean {
  return getReactBooleanEnv("REACT_APP_USE_SERVER_SESSION_AUTH", isReactStrictEnv());
}
